import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  Loader2,
  MessageSquarePlus,
  Bug,
  Lightbulb,
  HelpCircle,
  Gauge,
  FileWarning,
  Heart,
  ImagePlus,
  X,
  Star,
  LifeBuoy,
} from "lucide-react";
import { Link } from "react-router-dom";
import { requestTicket } from "@/lib/guest";
import {
  FEATURE_AREAS,
  FEEDBACK_TYPES,
  SEVERITIES,
  collectBrowserInfo,
  currentPagePath,
  detectFeatureArea,
  type FeedbackType,
} from "@/lib/feedbackContext";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Override the auto-detected feature area (e.g. from a specific panel). */
  area?: string;
}

const TYPE_ICON: Record<FeedbackType, typeof Bug> = {
  bug: Bug,
  feature: Lightbulb,
  confusing: HelpCircle,
  performance: Gauge,
  content: FileWarning,
  praise: Heart,
};

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export const FeedbackDialog = ({ open, onOpenChange, area }: FeedbackDialogProps) => {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [feedbackType, setFeedbackType] = useState<FeedbackType | "">("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [featureArea, setFeatureArea] = useState<string>("");
  const [severity, setSeverity] = useState<string>("annoying");
  const [rating, setRating] = useState<number>(0);
  const [contactEmail, setContactEmail] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);

  const pagePath = useMemo(() => (open ? currentPagePath() : ""), [open]);

  useEffect(() => {
    if (!open) return;
    setFeatureArea(area ?? detectFeatureArea());
  }, [open, area]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = () => {
    setFeedbackType("");
    setTitle("");
    setMessage("");
    setSeverity("annoying");
    setRating(0);
    setContactEmail("");
    setFile(null);
    setIncludeDiagnostics(true);
  };

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Screenshots only — please pick an image file.");
      return;
    }
    if (f.size > MAX_SCREENSHOT_BYTES) {
      toast.error("That image is over 5 MB. Try a cropped screenshot.");
      return;
    }
    setFile(f);
  };

  const uploadScreenshot = async (): Promise<string | null> => {
    if (!file || !user) return null;
    const ext = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
    const path = `${user.id}/feedback-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("feedback-media").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });
    if (error) throw error;
    return path;
  };

  const handleSubmit = async () => {
    if (!user) {
      requestTicket("send feedback");
      return;
    }
    if (!feedbackType) {
      toast.error("Pick what kind of note this is");
      return;
    }
    if (title.trim().length < 4) {
      toast.error("Give it a short headline (4+ characters)");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Please write at least 10 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      // A failed screenshot upload must never lose the written note.
      let screenshotPath: string | null = null;
      if (file) {
        try {
          screenshotPath = await uploadScreenshot();
        } catch {
          toast.warning("We couldn't attach that screenshot — sending your note without it.");
        }
      }

      const { error } = await supabase.from("feedback" as any).insert({
        user_id: user.id,
        feedback_type: feedbackType,
        title: title.trim().slice(0, 160),
        message: message.trim(),
        page_path: includeDiagnostics ? pagePath : null,
        feature_area: featureArea || null,
        severity: feedbackType === "bug" || feedbackType === "performance" ? severity : null,
        rating: rating > 0 ? rating : null,
        browser_info: includeDiagnostics
          ? collectBrowserInfo() as unknown as Record<string, unknown>
          : {},
        screenshot_url: screenshotPath,
        contact_email: contactEmail.trim() || null,
      } as any);

      if (error) throw error;

      toast.success("Filed with the projectionist — thank you! 🎬");
      reset();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsSeverity = feedbackType === "bug" || feedbackType === "performance";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[94vw] max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-accent">Comment card</span>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            Leave a note for the projectionist
          </DialogTitle>
          <DialogDescription>
            Tell us what worked, what broke, or what you wish existed. We read every card.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-1">
          {/* Type picker */}
          <div className="space-y-2">
            <Label>What kind of note is this?</Label>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_TYPES.map((t) => {
                const Icon = TYPE_ICON[t.value];
                const active = feedbackType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setFeedbackType(t.value)}
                    className={`rounded-xl border p-3 text-left transition-all ${
                      active
                        ? "border-primary/70 bg-primary/10 shadow-glow"
                        : "border-border/60 bg-card/50 hover:border-primary/40"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                      {t.label}
                    </span>
                    <span className="mt-1 block text-[11px] leading-snug text-muted-foreground">{t.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-title">Headline</Label>
            <Input
              id="fb-title"
              placeholder="Watchlist toggle doesn't stick"
              value={title}
              maxLength={160}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-message">Details</Label>
            <Textarea
              id="fb-message"
              placeholder={
                feedbackType === "bug"
                  ? "What did you do, what happened, and what did you expect instead?"
                  : "Give us as much detail as you like…"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={2000}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/2000</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Which part of the theatre?</Label>
              <Select value={featureArea} onValueChange={setFeatureArea}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick an area…" />
                </SelectTrigger>
                <SelectContent>
                  {FEATURE_AREAS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {needsSeverity && (
              <div className="space-y-2">
                <Label>How badly does it hurt?</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Rating */}
          <div className="space-y-2">
            <Label>How's the show so far? <span className="text-muted-foreground">(optional)</span></Label>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  onClick={() => setRating(rating === n ? 0 : n)}
                  className="rounded-md p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-6 w-6 ${n <= rating ? "fill-marquee-bulb text-marquee-bulb" : "text-muted-foreground/50"}`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Screenshot */}
          <div className="space-y-2">
            <Label>Attach a screenshot <span className="text-muted-foreground">(optional)</span></Label>
            {previewUrl ? (
              <div className="relative overflow-hidden rounded-xl border border-border/60">
                <img src={previewUrl} alt="Screenshot preview" className="max-h-40 w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-background/80 backdrop-blur"
                  aria-label="Remove screenshot"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border/70 px-4 py-5 text-sm text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
              >
                <ImagePlus className="h-4 w-4" />
                Add an image (max 5 MB)
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-email">Contact email <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="fb-email"
              type="email"
              placeholder="your@email.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>

          <label className="flex items-start gap-3 rounded-xl border border-border/50 bg-muted/20 p-3 text-sm">
            <Checkbox
              checked={includeDiagnostics}
              onCheckedChange={(checked) => setIncludeDiagnostics(Boolean(checked))}
              aria-label="Include diagnostic context"
            />
            <span>
              <span className="block font-medium text-foreground">Include diagnostic context</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                Adds the current Swiftix page and basic browser details. Never precise location.
              </span>
            </span>
          </label>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !feedbackType || title.trim().length < 4 || message.trim().length < 10}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Filing your card…
              </>
            ) : (
              "Drop it in the box"
            )}
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <LifeBuoy className="h-3.5 w-3.5" />
            Need an actual reply?{" "}
            <Link
              to="/support"
              onClick={() => onOpenChange(false)}
              className="font-semibold text-primary hover:underline"
            >
              Visit the Box Office
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
